import 'react-vis/dist/style.css';

import React from 'react';
import {LineSeries, XYPlot} from 'react-vis';

import {Icon} from '../../../../../components/Icon';
import {COLOR_PALETTE, formatTooltipValue} from './format';

type IconName = Parameters<typeof Icon>[0]['name'];

/**
 * Minimal sparkline plot used in summary cards with optional shared hover cursor.
 *
 * Args:
 *   data: Array of numeric values or nulls.
 *   width: Width in pixels.
 *   height: Height in pixels.
 *   color: Line color.
 *   hoverIndex: Optional shared hover index to render a vertical cursor.
 *
 * Returns:
 *   A sparkline chart element.
 */
const SparklinePlot: React.FC<{
  data: Array<number | null>;
  width?: number;
  height?: number;
  color?: string;
  hoverIndex?: number | null;
  showCursor?: boolean;
}> = ({
  data,
  width = 120,
  height = 28,
  color = COLOR_PALETTE[0],
  hoverIndex,
  showCursor = true,
}) => {
  const w = width;
  const h = height;
  const defined = data.filter(v => v !== null) as number[];
  const yMin = Math.min(...(defined.length ? defined : [0]));
  const yMax = Math.max(...(defined.length ? defined : [0]));
  const yDomain = yMin === yMax ? [yMin - 1, yMax + 1] : [yMin, yMax];
  const step = data.length > 1 ? w / (data.length - 1) : w;

  const segments: Array<Array<{x: number; y: number}>> = [];
  let current: Array<{x: number; y: number}> = [];
  data.forEach((v, i) => {
    if (v === null || v === undefined) {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
    } else {
      current.push({x: i, y: v});
    }
  });
  if (current.length > 0) {
    segments.push(current);
  }

  const hoverX =
    hoverIndex != null
      ? Math.max(0, Math.min(data.length - 1, hoverIndex)) * step
      : null;

  return (
    <div style={{position: 'relative', width: w, height: h}}>
      <XYPlot
        width={w}
        height={h}
        margin={{left: 0, right: 0, top: 0, bottom: 0}}
        xDomain={[0, Math.max(0, data.length - 1)]}
        yDomain={yDomain as [number, number]}>
        {segments.map((seg, i) => (
          <LineSeries key={i} data={seg} stroke={color} />
        ))}
      </XYPlot>
      {hoverX != null && showCursor && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: hoverX,
            width: 1,
            height: h,
            background: '#E5E7EB',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};

/**
 * Displays the current value and delta for a row.
 */
const SparklineTrend: React.FC<{
  value: number;
  delta: number;
  unit?: string;
  formatRowValue: (value: number, unit?: string) => string;
}> = ({value, delta, unit, formatRowValue}) => {
  const sign = delta >= 0 ? '+' : '-';
  const arrow = delta >= 0 ? '↗' : '↘';
  return (
    <div className="grid [grid-template-columns:auto_auto] items-center gap-4 justify-items-start">
      <div className="text-moon-950 text-[16px] font-semibold text-left whitespace-nowrap">
        {formatRowValue(value, unit)}
      </div>
      <div
        className={
          'text-moon-700 text-sm inline-flex items-center gap-1.5 text-left whitespace-nowrap'
        }>
        <span aria-hidden>{arrow}</span>
        <span>{`${sign} ${formatRowValue(Math.abs(delta), unit)}`}</span>
      </div>
    </div>
  );
};

/**
 * Renders the vertical hover cursor overlay aligned with sparklines.
 */
const SparklineHoverOverlay: React.FC<{
  left: number;
  width: number;
  hoverXPx: number;
}> = ({left, width, hoverXPx}) => {
  return (
    <div
      className="absolute inset-y-0 pointer-events-none z-10"
      style={{left, width}}>
      <div
        className="absolute inset-y-0 w-[2px] bg-[#9CA3AF] opacity-90"
        style={{left: hoverXPx}}
      />
    </div>
  );
};

/**
 * A single sparkline row with label, value/delta, and mini plot.
 */
const SparklineRow: React.FC<{
  row: SparklineCardRow;
  idx: number;
  seriesLength: number;
  hoverIdx: number | null;
  sparkWidth: number | null;
  setActiveRowIdx: (idx: number | null) => void;
  firstSparkRef: React.RefObject<HTMLDivElement>;
  formatRowValue: (value: number, unit?: string) => string;
}> = ({
  row,
  idx,
  seriesLength,
  hoverIdx,
  sparkWidth,
  setActiveRowIdx,
  firstSparkRef,
  formatRowValue,
}) => {
  const series = row.series;
  const getValueAtIndexLocal = (
    seriesLocal: Array<number | null>,
    index: number
  ) => {
    if (seriesLocal[index] !== null && seriesLocal[index] !== undefined)
      return seriesLocal[index]!;
    for (let i = index - 1; i >= 0; i--) {
      if (seriesLocal[i] !== null && seriesLocal[i] !== undefined)
        return seriesLocal[i]!;
    }
    for (let i = index + 1; i < seriesLocal.length; i++) {
      if (seriesLocal[i] !== null && seriesLocal[i] !== undefined)
        return seriesLocal[i]!;
    }
    return 0;
  };

  const current =
    seriesLength > 0 ? getValueAtIndexLocal(series, seriesLength - 1) : 0;
  const firstDefined = (() => {
    for (let i = 0; i < series.length; i++) {
      if (series[i] != null) return series[i]!;
    }
    return 0;
  })();
  const delta =
    (typeof current === 'number' ? Number(current) : 0) -
    (typeof firstDefined === 'number' ? Number(firstDefined) : 0);
  return (
    <div className="grid [grid-template-columns:minmax(0,1fr)_180px_minmax(120px,1fr)] items-center py-8">
      <div
        className={`grid items-center ${
          row.icon ? '[grid-template-columns:24px_1fr]' : 'grid-cols-1'
        } gap-1.5 min-w-0`}>
        {row.icon ? <Icon name={row.icon} /> : null}
        <div className="text-moon-700 text-base overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
          {row.label}
        </div>
      </div>
      <SparklineTrend
        value={typeof current === 'number' ? current : Number(current)}
        delta={delta}
        unit={row.unit}
        formatRowValue={formatRowValue}
      />
      <div
        className="flex justify-end cursor-pointer"
        ref={idx === 0 ? firstSparkRef : undefined}
        onMouseEnter={() => setActiveRowIdx(idx)}
        onMouseLeave={() => setActiveRowIdx(null)}>
        <SparklinePlot
          data={series}
          color={row.color || COLOR_PALETTE[0]}
          hoverIndex={hoverIdx}
          width={sparkWidth ?? 120}
          showCursor={false}
        />
      </div>
    </div>
  );
};

/**
 * Tooltip for the shared hover state across rows.
 */
const SparklineTooltip: React.FC<{
  hoverIdx: number;
  tooltipPos: {x: number; y: number};
  rows: SparklineCardRow[];
  formatTooltipValueFn: (
    value: number | null,
    unit?: string,
    rowId?: string,
    hoverIdx?: number
  ) => string;
  tooltipHeaderRenderer?: (hoverIndex: number) => React.ReactNode;
  tooltipMaxWidth?: number;
  tooltipMinWidth?: number;
  onSparklineClick?: (hoverIndex: number) => void;
  activeRowIdx: number | null;
}> = ({
  hoverIdx,
  tooltipPos,
  rows,
  formatTooltipValueFn,
  tooltipHeaderRenderer,
  tooltipMaxWidth,
  tooltipMinWidth,
  onSparklineClick,
  activeRowIdx,
}) => {
  return (
    <div
      className="absolute -translate-x-full bg-white border border-moon-300 rounded-md shadow-md p-8 z-10 pointer-events-none"
      style={{
        left: tooltipPos.x,
        top: tooltipPos.y,
        maxWidth: tooltipMaxWidth ?? 280,
        minWidth: tooltipMinWidth,
      }}>
      {onSparklineClick ? (
        <div className="text-moon-700 absolute right-3 top-1 opacity-80 text-[10px] whitespace-nowrap">
          Click to view this evaluation
        </div>
      ) : null}
      {tooltipHeaderRenderer ? (
        <div className="mb-1 pr-[96px]">{tooltipHeaderRenderer(hoverIdx)}</div>
      ) : null}
      <div
        className={`grid [grid-template-columns:auto_1fr_auto_auto] gap-x-8 gap-y-1.5 items-center ${
          tooltipHeaderRenderer ? 'mt-1.5' : ''
        }`}>
        {rows.map((row, idx) => {
          const v =
            hoverIdx >= 0 && hoverIdx < row.series.length
              ? (row.series[hoverIdx] as number | null)
              : null;
          const formattedValue = formatTooltipValueFn(
            v,
            row.unit,
            row.id,
            hoverIdx
          );
          // Split percentage and count parts for better alignment
          const percentMatch = formattedValue.match(/^(.+?)(\s+\(.+\))$/);
          const mainValue = percentMatch ? percentMatch[1] : formattedValue;
          const countPart = percentMatch ? percentMatch[2].trim() : '';

          return (
            <React.Fragment key={row.id}>
              <div
                className="w-10 h-10 rounded-full border border-moon-300"
                style={{
                  background: row.color || COLOR_PALETTE[0],
                  opacity:
                    activeRowIdx != null && activeRowIdx !== idx ? 0.8 : 1,
                }}
              />
              <div
                className="text-moon-700 text-xs"
                style={{
                  fontWeight: activeRowIdx === idx ? 600 : 400,
                  opacity:
                    activeRowIdx != null && activeRowIdx !== idx ? 0.8 : 1,
                }}>
                {row.label}
              </div>
              <div
                className="text-moon-950 text-xs text-right"
                style={{
                  fontWeight: activeRowIdx === idx ? 600 : 400,
                  opacity:
                    activeRowIdx != null && activeRowIdx !== idx ? 0.8 : 1,
                }}>
                {mainValue}
              </div>
              <div
                className="text-moon-700 text-xs"
                style={{
                  fontWeight: activeRowIdx === idx ? 600 : 400,
                  opacity:
                    activeRowIdx != null && activeRowIdx !== idx ? 0.8 : 1,
                  minWidth: countPart ? '60px' : '0px',
                }}>
                {countPart}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export type SparklineCardRow = {
  id: string;
  label: string;
  icon?: IconName;
  color?: string;
  series: Array<number | null>;
  unit?: string;
};

export type SparklineCardProps = {
  header?: React.ReactNode;
  rows: SparklineCardRow[];
  formatCurrentValue?: (value: number, unit?: string) => string;
  formatTooltipValueFn?: (
    value: number | null,
    unit?: string,
    rowId?: string,
    hoverIdx?: number
  ) => string;
  tooltipHeaderRenderer?: (hoverIndex: number) => React.ReactNode;
  tooltipMaxWidth?: number;
  tooltipMinWidth?: number;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onSparklineClick?: (hoverIndex: number) => void;
};

/**
 * Card component that renders label/value rows with small sparklines and a shared hover tooltip.
 *
 * Args:
 *   header: Optional header content rendered at the top of the card.
 *   rows: Rows to render, each with label, icon, color, and numeric series.
 *   formatCurrentValue: Formatter for the main row value and delta text.
 *   formatTooltipValueFn: Formatter for tooltip values. Defaults to formatTooltipValue.
 *   tooltipHeaderRenderer: Optional renderer for the tooltip header based on hover index.
 *
 * Returns:
 *   A card element with rows and shared-hover tooltip.
 *
 * Examples:
 *   <SparklineCard rows={[{id:'a',label:'Latency',series:[1,2,3]}]} />
 */
export const SparklineCard: React.FC<SparklineCardProps> = ({
  header,
  rows,
  formatCurrentValue,
  formatTooltipValueFn = formatTooltipValue,
  tooltipHeaderRenderer,
  tooltipMaxWidth,
  tooltipMinWidth,
  collapsible = false,
  defaultCollapsed = false,
  onCollapsedChange,
  onSparklineClick,
}) => {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [hoverIdx, setHoverIdx] = React.useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [collapsed, setCollapsed] = React.useState<boolean>(defaultCollapsed);
  const rowsContainerRef = React.useRef<HTMLDivElement>(null);
  const firstSparkRef = React.useRef<HTMLDivElement>(null);
  const [hoverXPx, setHoverXPx] = React.useState<number | null>(null);
  const [sparkOverlay, setSparkOverlay] = React.useState<{
    left: number;
    width: number;
  } | null>(null);
  const [sparkWidth, setSparkWidth] = React.useState<number | null>(null);
  const [activeRowIdx, setActiveRowIdx] = React.useState<number | null>(null);

  const seriesLength = rows[0]?.series?.length ?? 0;

  const formatRowValue =
    formatCurrentValue ??
    ((value: number, unit?: string) => formatTooltipValue(value, unit));

  const handleCardMouseMove: React.MouseEventHandler<HTMLDivElement> = e => {
    if (!firstSparkRef.current || !cardRef.current || !rowsContainerRef.current)
      return;
    const sparkRect = firstSparkRef.current.getBoundingClientRect();
    const cardRect = cardRef.current.getBoundingClientRect();
    const rowsRect = rowsContainerRef.current.getBoundingClientRect();

    // Only activate hover when inside the sparkline column bounds
    const insideX = e.clientX >= sparkRect.left && e.clientX <= sparkRect.right;
    const insideY = e.clientY >= rowsRect.top && e.clientY <= rowsRect.bottom;
    if (!insideX || !insideY) {
      if (hoverIdx != null || tooltipPos != null || hoverXPx != null) {
        setHoverIdx(null);
        setTooltipPos(null);
        setHoverXPx(null);
      }
      return;
    }

    const width = sparkRect.width || 1;
    const step = seriesLength > 1 ? width / (seriesLength - 1) : width;
    const xWithinSpark = Math.max(
      0,
      Math.min(width, e.clientX - sparkRect.left)
    );
    const idx = seriesLength > 1 ? Math.round(xWithinSpark / step) : 0;
    setHoverIdx(idx);

    setHoverXPx(xWithinSpark);
    setSparkOverlay({left: sparkRect.left - rowsRect.left, width});
    // Add horizontal gap so tooltip doesn't hug the cursor line
    // Fixed Y within the card; keep X following the cursor
    setTooltipPos({
      x: sparkRect.left - cardRect.left + xWithinSpark - 12,
      y: 12,
    });

    // Also determine the nearest row by Y position to avoid gaps causing flicker
    let nearestIdx = 0;
    let minDist = Infinity;
    const children = rowsContainerRef.current?.children || [];
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      const rect = child.getBoundingClientRect();
      const centerY = (rect.top + rect.bottom) / 2;
      const dist = Math.abs(e.clientY - centerY);
      if (dist < minDist) {
        minDist = dist;
        nearestIdx = i;
      }
    }
    setActiveRowIdx(nearestIdx);
  };

  const handleCardMouseLeave: React.MouseEventHandler<HTMLDivElement> = () => {
    setHoverIdx(null);
    setTooltipPos(null);
    setHoverXPx(null);

    setActiveRowIdx(null);
  };

  const handleCardClick: React.MouseEventHandler<HTMLDivElement> = e => {
    if (!firstSparkRef.current || hoverIdx == null || !onSparklineClick) return;
    const sparkRect = firstSparkRef.current.getBoundingClientRect();
    if (e.clientX >= sparkRect.left && e.clientX <= sparkRect.right) {
      onSparklineClick(hoverIdx);
    }
  };

  React.useEffect(() => {
    const el = firstSparkRef.current;
    if (!el) return;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (!isNaN(w) && w > 0) setSparkWidth(Math.floor(w));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed, rows.length]);

  const handleToggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      if (onCollapsedChange) onCollapsedChange(next);
      if (next) {
        // Clear tooltip/hover when collapsing
        setHoverIdx(null);
        setTooltipPos(null);
      }
      return next;
    });
  };

  const handleHeaderKeyDown: React.KeyboardEventHandler<HTMLDivElement> = e => {
    if (!collapsible) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleCollapsed();
    }
  };

  return (
    <div
      ref={cardRef}
      className="bg-white border border-moon-300 rounded-lg py-[12px] px-[16px] relative">
      {(header || collapsible) && (
        <div
          role={collapsible ? 'button' : undefined}
          tabIndex={collapsible ? 0 : undefined}
          aria-expanded={collapsible ? !collapsed : undefined}
          onClick={collapsible ? handleToggleCollapsed : undefined}
          onKeyDown={collapsible ? handleHeaderKeyDown : undefined}
          className={`flex items-center justify-between py-[6px] px-2 text-moon-800 font-semibold ${
            collapsible ? 'cursor-pointer' : ''
          }`}>
          {typeof header === 'string' ? (
            <div className="text-sm">{header}</div>
          ) : (
            header
          )}
          {collapsible ? (
            <button
              onClick={e => {
                e.stopPropagation();
                handleToggleCollapsed();
              }}
              className="inline-flex items-center p-1 bg-transparent border-0 cursor-pointer"
              aria-label={collapsed ? 'Expand' : 'Collapse'}>
              <Icon name={collapsed ? 'chevron-next' : 'chevron-down'} />
            </button>
          ) : null}
        </div>
      )}
      {!collapsed && (
        <div
          ref={rowsContainerRef}
          onMouseMove={handleCardMouseMove}
          onMouseLeave={handleCardMouseLeave}
          onClick={handleCardClick}
          className="relative grid">
          {rows.map((row, idx) => (
            <SparklineRow
              key={row.id}
              row={row}
              idx={idx}
              seriesLength={seriesLength}
              hoverIdx={hoverIdx}
              sparkWidth={sparkWidth}
              setActiveRowIdx={setActiveRowIdx}
              firstSparkRef={firstSparkRef}
              formatRowValue={formatRowValue}
            />
          ))}
          {sparkOverlay && hoverXPx != null && (
            <SparklineHoverOverlay
              left={sparkOverlay.left}
              width={sparkOverlay.width}
              hoverXPx={hoverXPx}
            />
          )}
        </div>
      )}
      {hoverIdx != null && tooltipPos && !collapsed && (
        <SparklineTooltip
          hoverIdx={hoverIdx}
          tooltipPos={tooltipPos}
          rows={rows}
          formatTooltipValueFn={formatTooltipValueFn}
          tooltipHeaderRenderer={tooltipHeaderRenderer}
          tooltipMaxWidth={tooltipMaxWidth}
          tooltipMinWidth={tooltipMinWidth}
          onSparklineClick={onSparklineClick}
          activeRowIdx={activeRowIdx}
        />
      )}
    </div>
  );
};

export default SparklineCard;
